import {Component, HostListener, OnInit} from '@angular/core';
import {Permission, Profile} from "../../../../model/profile";
import {ActivatedRoute, NavigationExtras, Router} from "@angular/router";
import {ProfilesService} from "../../../../services/profiles.service";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {noSpecialCharacters, noWhitespaces} from "../../../../shared/custom-validators";
import {AuthGuard} from "../../../../common/auth-guard.service";
import {PermissionService} from "../../../../services/permission.service";
import {ProfilePersistRequest} from "../../../../model/requests/profile-persist-request";
import {CommonMessagesService} from "../../../../services/common-messages.service";
import {AppConfig} from "../../../../shared/global-variables";

@Component({
  selector: 'app-profile-form',
  templateUrl: './profile-form.component.html',
  styleUrls: ['./profile-form.component.scss']
})
export class ProfileFormComponent implements OnInit {

  public mode: 'create' | 'edit';

  public isProfileLoading: boolean = true;
  public loadedData: boolean;

  public profileForm: FormGroup;
  public persistingChanges: boolean;

  public availablePermissions: Permission[] = [];
  public targetPermissions: Permission[] = [];

  private profile: Profile;
  private allPermissions: Permission[] = [];

  private formSuccess: boolean;


  constructor(private profileService: ProfilesService,
              private route: ActivatedRoute,
              private router: Router,
              private formBuilder: FormBuilder,
              private commonMessagesService: CommonMessagesService,
              public guard: AuthGuard,
              private permissionService: PermissionService) {
  }

  ngOnInit(): void {
    this.loadDataAndForm();
  }

  private loadDataAndForm(): void {
    this.isProfileLoading = true;
    this.loadedData = false;
    const promises = [
      this.permissionService.getPermissions().toPromise().then(response => {
        this.allPermissions = response.permissions;
      })
    ];

    Promise.all(promises).then(() => {
      this.route.url.subscribe(urlSegment => {
        const firstPath = urlSegment[1].path;
        if (firstPath === 'new') {
          this.mode = 'create';
          this.loadCreationForm();
        } else {
          this.mode = 'edit';
          this.loadEditionForm(firstPath);
        }
      });
    }).finally(() => this.isProfileLoading = false);
  }

  loadCreationForm(): void {
    this.profile = new Profile();
    this.availablePermissions = this.allPermissions;
    this.createForm(this.profile);
  }

  loadEditionForm(profileUuid: string): void {
    this.profileService.getProfile(profileUuid).toPromise()
      .then(response => {
        this.profile = response.profile;
        if (this.profile.permissions != null && this.profile.permissions.length > 0) {
          let targetPermissionsUuids: string[] = this.profile.permissions
            .map((permission: Permission) => permission.permissionUuid);
          this.availablePermissions = this.allPermissions
            .filter((permission: Permission) => !targetPermissionsUuids.includes(permission.permissionUuid));
          this.targetPermissions = this.allPermissions
            .filter((permission: Permission) => targetPermissionsUuids.includes(permission.permissionUuid));

        } else {
          this.availablePermissions = this.allPermissions;
        }

        this.createForm(this.profile);
        this.profileForm.addControl('uuid', new FormControl(this.profile.profileUuid,
          [Validators.required, Validators.maxLength(36)]));
        if (this.profile.predefined) {
          this.profileForm.get('name').disable();
        }
      }, error => {
        if (error.status == 404) {
          this.router.navigate(['/not-found']);
        }
      });
  }

  private createForm(profile: Profile): void {
    this.loadedData = true;
    this.profileForm = this.formBuilder.group({
      name: [profile.name, [Validators.maxLength(255), Validators.minLength(AppConfig.FORM_IDENTIFIER_MIN_LENGTH),
        Validators.required, noSpecialCharacters, noWhitespaces]],
      description: [profile.description, [Validators.maxLength(255)]],
      profileUuid: this.mode === 'edit' ? this.profile.profileUuid : null
    });
    this.profileForm.markAsPending();
  }

  persistProfileData(): void {
    this.persistingChanges = true;
    const persistData: ProfilePersistRequest = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.profileService.createProfile(persistData).toPromise();
    } else {
      promise = this.profileService.editProfile(persistData).toPromise();
    }

    promise.then(() => {
      if (this.mode === 'create') {
        this.commonMessagesService.showSuccessfullyCreatedMessage();
      } else {
        this.commonMessagesService.showSuccessfullyUpdatedMessage();
      }
      this.formSuccess = true;
      this.router.navigate((['/users']), {state: {showView: 'profiles'}});
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): ProfilePersistRequest {
    const formValue = this.profileForm.getRawValue();
    return {
      name: formValue.name?.trim(),
      description: formValue.description?.trim(),
      permissionUuids: this.getTargetPermissionsUuids(),
      profileUuid: this.mode === 'edit' ? this.profile.profileUuid : null
    };
  }

  private getTargetPermissionsUuids(): string[] {
    return this.targetPermissions?.length > 0 ? this.targetPermissions.map(permission => permission.permissionUuid) : [];
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || (this.profileForm?.untouched && this.permissionsUntouched());
  }

  permissionsUntouched(): boolean {
    if (this.mode === 'create') {
      return this.targetPermissions?.length === 0;
    } else {
      let newPermissionsUuids: string[] = this.getTargetPermissionsUuids().sort((a, b) => a.localeCompare(b));
      let oldPermissionsUuids: string[] = this.profile?.permissions ?
        this.profile.permissions.map(permission => permission.permissionUuid).sort((a, b) => a.localeCompare(b)) : [];
      return JSON.stringify([...new Set(oldPermissionsUuids)]) === JSON.stringify([...new Set(newPermissionsUuids)]);
    }
  }

  public cancel(): void {
    const extras: NavigationExtras = {state: {showView: 'profiles'}};
    this.router.navigate(['/users'], extras);
  }

}
